/**
 * Teknik Account authentication service
 */
export class TeknikAuthService {
  private static readonly AUTH_KEY = 'teknik_account_data';
  private static readonly AUTH_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days

  static isAuthenticated(): boolean {
    try {
      const raw = localStorage.getItem(this.AUTH_KEY);
      if (!raw) return false;
      const { timestamp, user } = JSON.parse(raw);
      if (user && Date.now() - timestamp < this.AUTH_EXPIRY) return true;
      this.clearLocal();
      return false;
    } catch {
      return false;
    }
  }

  static getCurrentUser(): any {
    try {
      const raw = localStorage.getItem(this.AUTH_KEY);
      if (!raw) return null;
      const { user, timestamp } = JSON.parse(raw);
      if (user && Date.now() - timestamp < this.AUTH_EXPIRY) return user;
      this.clearLocal();
      return null;
    } catch {
      return null;
    }
  }

  static setLocalData(user: any): void {
    try {
      localStorage.setItem(this.AUTH_KEY, JSON.stringify({ user, timestamp: Date.now() }));
    } catch {}
  }

  static clearLocal(): void {
    localStorage.removeItem(this.AUTH_KEY);
  }

  /** Register a new Teknik Account */
  static async register(email: string, username: string, password: string, referralCode?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/auth/teknik/register', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, username, password, referralCode })
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Registration failed.' };
      if (data.user) this.setLocalData(data.user);
      return { success: true };
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /** Login with email + password */
  static async login(email: string, password: string): Promise<{ success: boolean; requiresTwoFactor?: boolean; error?: string }> {
    try {
      const res = await fetch('/auth/teknik/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Login failed.' };
      if (data.requiresTwoFactor) return { success: true, requiresTwoFactor: true };
      if (data.user) this.setLocalData(data.user);
      return { success: true };
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  /** Start the Discord link flow */
  static linkDiscord(): void {
    window.location.href = '/auth/teknik/link-discord';
  }

  /** Unlink Discord from the current Teknik account */
  static async unlinkDiscord(): Promise<{ success: boolean; error?: string }> {
    try {
      const res = await fetch('/auth/teknik/unlink-discord', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) return { success: false, error: data.error || 'Failed to unlink.' };
      return { success: true };
    } catch {
      return { success: false, error: 'Network error. Please try again.' };
    }
  }
}

export default TeknikAuthService;
